import React, { useMemo, useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Waypoint } from 'react-waypoint';
import { 
  Stack, 
  HStack, 
  Avatar,
  Text,
  Icon,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Skeleton, 
  SkeletonCircle, 
  SkeletonText,
  useToast
} from '@chakra-ui/react'
import { MdEdit, MdLock, MdStar, MdFlag } from 'react-icons/md';
import { RiThumbUpFill, RiThumbUpLine } from "react-icons/ri";
import { AiFillThunderbolt } from 'react-icons/ai';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useUser, useGlobal } from 'src/context';
import { postReviewFeedback } from "src/api";

import { userTitleMap, trackEvent } from 'utils';

// TODO: 部分行為可從 context 不必 props

dayjs.extend(relativeTime)

const TOGGLE_INTERVAL = 2 * 1000;
const TEN_DAYS = 10;


const getFormattedDate = timestamp => {
  if (timestamp) return dayjs(timestamp).format('YYYY-MM-DD');
}

export const CommentSkeleton = () => {
  return (
    <HStack px={3} spacing={3}>
      <SkeletonCircle size={12} />
      <Stack spacing={1} flexGrow={1}>
        <Skeleton h={4} w="40%" />
        <Skeleton h={4} w="30%" />
        <SkeletonText noOfLines={2} spacing={1} skeletonHeight={3} />
      </Stack>
    </HStack>
  )
};

/**
 * @param {boolean} isOwn 是否為會員自己的評價，差異在於舉報按鈕替換成編輯
 * @param {string} customId 會員頁面的連結參數
 * @param {boolean} isPublicUserPage 是否產生前往會員頁面的連結
 * @param {number} playDate unix timestamp，format顯示日期使用
 * @param {number} lastModifiedTime unix timestamp，format顯示距今天數使用
 */

const CommentCard = props => {
  const { 
    gameId, 
    title, 
    customId, 
    userName, 
    userTitle, 
    userTitleText, 
    userPhotoUrl, 
    rating, 
    comment, 
    playDate, 
    feedbackPoints, // number | undefined
    feedback, // 1 or null
    lastModifiedTime, 
    createTime,
    isPublicUserPage, 
    isHidden, 
    isSpoiler,
    isOwn,
    onEdit,
    onReport, 
  } = props;

  const [hasFeedbacked, setHasFeedbacked] = useState(false);
  const [score, setScore] = useState(0);

  const { isLogin } = useUser();
  const { onOpenLogin } = useGlobal();

  const toast = useToast();

  const lastToggleTimestamp = useRef(0);

  const lastModifiedDayObj = dayjs(lastModifiedTime);
  const createDayObj = createTime ? dayjs(createTime) : null;
  const isModifiedInOneMonth = lastModifiedTime > createTime && dayjs().diff(lastModifiedDayObj, 'day') <= TEN_DAYS * 3;
  const isShowCreateDate = !playDate && createDayObj && isModifiedInOneMonth && lastModifiedDayObj.diff(createDayObj, 'day') > TEN_DAYS;

  // 因 globally 改變 locale 會造成 react-tailwindcss-datepicker 顯示異常，所以必須在用到的地方 locally 設定；若此問題修正再回來修改此處
  const displayLastModifiedDate = lastModifiedDayObj.locale('zh-tw').fromNow();

  const displayTitle = useMemo(() => {
    let displayText = "";

    if (userTitleText) {
      displayText = userTitleText;
    } else if (userTitle !== '' && userTitle !== "none") {
      // 早期版本此兩條件表示不顯示
      displayText = userTitleMap[userTitle].shortDisplayText;
    }

    return displayText;
  }, [userTitleText, userTitle]);

  useEffect(() => {
    if (feedbackPoints > 0) {
      setScore(feedbackPoints);
    }
  }, [feedbackPoints]);

  useEffect(() => {
    if (feedback) {
      setHasFeedbacked(true);
    }
  }, [feedback])
  
  const handleReportClick = (reason) => {
    onReport({ customId, userName, reason, comment });
  };

  const handleEditClick = () => {
    onEdit();
  };

  const handleHelpfulToggle = async () => {
    trackEvent({
      category: 'GameReview_feedback',
      action: 'Click',
      label: `${gameId}-${title}-${userName}`,
    });

    const timestamp = Date.now();

    if (timestamp - lastToggleTimestamp.current < TOGGLE_INTERVAL) {
      toast({
        title: '請勿頻繁進行此操作',
        position: 'top',
        status: 'warning',
        duration: 1500,
      })

      return;
    }

    if (isOwn) return;

    if (!isLogin) {
      onOpenLogin();
      return;
    }
    
    try {
      lastToggleTimestamp.current = timestamp;

      await postReviewFeedback({ 
        gameId,
        customId
      })

      setScore(prev => hasFeedbacked ? prev - 1 : prev + 1);
      setHasFeedbacked(prev => !prev);
    } catch (err) {
      console.log(err)
    }
  };

  const handleEnterView = () => {
    if (!isOwn) trackEvent({ category: 'GameReview', action: 'View', label: `${gameId}-${title}` })
  };

  const renderLinkComponent = componentName => {

    const component = componentName === 'avatar' 
      ? <Avatar name={userName} src={userPhotoUrl} />
      : <Text fontSize='sm' color='primary.500' fontWeight='700' >{userName}</Text>
      ;

    return isPublicUserPage 
      ? (
          <Link href={`/user/${customId}`}>
            {component}
          </Link>
        ) 
      : component
      ;
  };

  return (
    <Waypoint onEnter={handleEnterView}>
      <div className={`px-2 py-2 lg:px-3 w-full rounded-sm ${isOwn ? "bg-gray-100" : "bg-white"}`}>
        <div className='flex gap-x-3'>
          <div>
            {renderLinkComponent('avatar')}
          </div>
          <div className='flex-grow min-w-0 flex flex-col gap-y-1'>
            <div className='flex justify-between items-center'>
              <div className='flex items-center'>
                {renderLinkComponent('name')}
                {displayTitle && (
                  <Text color='secondary.500' fontSize='sm' fontWeight={600} noOfLines={1}>
                    ・{displayTitle}
                  </Text>
                )}
              </div>
            </div>

            <div className='flex gap-x-2 lg:gap-x-4'>
              <RatingStars rating={rating} />
              <span className='text-sm text-gray-500'>{displayLastModifiedDate} {isModifiedInOneMonth && '更新'}</span>
            </div>

            <div className={`flex flex-col gap-y-2 ${comment.length > 0 || playDate > 0 ? "" : "hidden"}`}>
              {isSpoiler && (
                <p className='text-secondary text-sm text-center font-semibold flex items-center gap-x-1'>
                  <Icon as={AiFillThunderbolt} boxSize={4} />
                  此評價內容可能含有劇透，請謹慎參考
                </p>
              )}
              {comment.length > 0 && <p className='text-gray-800 text-sm whitespace-pre-line'>{comment}</p>}
              {(playDate > 0 || isShowCreateDate) && (
                <p className='text-gray-500 text-sm'>
                  {playDate > 0 ? `遊玩日期：${getFormattedDate(playDate)}` : `首次發表日期： ${getFormattedDate(createTime)}`}
                </p>
              )}
              <div className='flex flex-wrap gap-x-4'>
                <Button 
                  leftIcon={hasFeedbacked ? <RiThumbUpFill /> : <RiThumbUpLine />} 
                  colorScheme='primary' 
                  variant='ghost'
                  size='sm'
                  onClick={handleHelpfulToggle}
                  isDisabled={isOwn}
                >
                  有幫助 {score > 0 && `(${score})`}
                </Button>

                {(isLogin && !isOwn) && (
                  <Menu isLazy>
                    <MenuButton
                      as={Button}
                      leftIcon={<MdFlag />} 
                      colorScheme='red' 
                      variant='ghost'
                      size='sm'
                    >
                      檢舉
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={() => handleReportClick('spoiler')}>回報含有劇透（暴雷）</MenuItem>
                      <MenuItem onClick={() => handleReportClick('fake')}>回報含有不實資訊</MenuItem>
                      <MenuItem onClick={() => handleReportClick('abuse')}>回報含有粗暴言論</MenuItem>
                    </MenuList>
                  </Menu>
                )}

                {isOwn && (
                  <>
                    <Button
                      leftIcon={<MdEdit />} 
                      variant='ghost'
                      size='sm'
                      onClick={handleEditClick}
                    >
                      編輯
                    </Button>

                    {isHidden && (
                      <p className='flex items-center gap-2 text-gray-500 text-sm'><Icon as={MdLock} boxSize={4} />由於權限設定，只有你自己看得到這則評價</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Waypoint>
  )
};

// 因為 @smastrom/react-rating 這個 library 不知為何在這邊會有第一次 load 不出來的問題，但因為是 readonly 所以可以自己用 Icon 拼一個
const RatingStars = ({ rating }) => {
  if (rating <= 0) return;

  const stars = Array(rating).fill('');

  return (
    <div>
      {stars.map((s, idx) => <Icon key={`star-${idx}`} as={MdStar} boxSize={5} color='orange.300' />)}
    </div>
  )
};

export default CommentCard;